/**
 * Comprehensive Test Data Loader Module
 * Centralizes all test data loading for development mode
 * Removes test concepts from the main store
 */

// Import test articles
import getTestArticleSet from './testArticleLoader.js';

// Import other test data
import pdfTestIssue from './Pdf/pdfTestIssue.js';
import audiobookTestIssue from './Audiobook/audiobookTestIssue.js';
import epubTestIssue from './Epub/epubTestIssue.js';
import testTableOfContents from './Pdf/testTableOfContents.js';
import testFilesUrls from './Pdf/testFilesUrls.js';
import testFilesUrlsStress532 from './Pdf/testFilesUrlsWith532Pages.js';
import audiobookTestPlaylist from './Audiobook/audiobookTestPlaylist.js';
import pdfTestHighlights from './Pdf/pdfTestHighlights.js';
import epubTestHighlights from './Epub/epubTestHighlights.js';

// Import EPUB test configuration
import { getTestEpubFile } from './Epub/testEpubFiles.js';

// Standalone dev has no host to send `richTextEmbedHosts`, so seed the same
// hosts here to exercise the paste-to-embed menu and the note editor's video
// and audio lists.
const RICH_TEXT_EMBED_HOSTS = {
    all: ['youtube.com', 'vimeo.com', 'sketchfab.com', 'open.spotify.com', 'soundcloud.com'],
    video: ['youtube.com', 'vimeo.com'],
    audio: ['open.spotify.com', 'soundcloud.com'],
};

/**
 * Get complete test data for development mode
 * @returns {Object} Complete test data object ready to be committed to store
 */
export function getTestData() {
    // Get the appropriate test article set based on URL params
    const articles = getTestArticleSet();

    // Determine file type from URL params or default to PDF. A type with no
    // viewer keeps the PDF fixtures, which the unsupported screen never renders.
    const urlParams = new URLSearchParams(window.location.search);
    const fileType = urlParams.get('file_type') || 'pdf';

    // Select PDF page set: default (local copesa) or stress-532 (532-page stress test)
    const pdfSet = urlParams.get('pdf_set');
    const pdfFilesUrls = pdfSet === 'stress-532' ? testFilesUrlsStress532 : testFilesUrls;

    // Select appropriate test data based on file type (explicit for clarity)
    const issue = fileType === 'audio' ? audiobookTestIssue : fileType === 'epub' ? epubTestIssue : pdfTestIssue;
    const highlights = fileType === 'epub' ? epubTestHighlights : pdfTestHighlights;

    // Get EPUB configuration if file type is EPUB
    const epubConfig = fileType === 'epub' ? getTestEpubFile() : null;

    return {
        // Issue data
        issue,

        // Publication data
        publication: {
            title: 'Volpe - DEMO',
            table_of_contents: testTableOfContents,
            files_urls: pdfFilesUrls,
            files_info: pdfFilesUrls,
            cover: issue.cover,
            url: null,
        },

        // Add-ons
        articles,

        // Audiobook data
        audiobook: {
            playlist: audiobookTestPlaylist,
            chapters: null,
            duration: null,
            currentTrackDurationInSeconds: null,
            speed: 1,
            track: {
                durationInSeconds: null,
            },
            trackPlayer: {
                isLoading: false,
                isPlaying: false,
            },
        },

        // Highlights/selections
        highlights,

        // Promote app data
        promoteApp: {
            enabled: true,
            android_app_link: 'https://play.google.com/store/apps/details?id=la.publica.reader',
            apple_app_link: 'https://apps.apple.com/app/apple-store/id1601992631?pt=119056122',
            app_icon: 'https://i.ibb.co/rcGxxLH/logo-app.jpg',
            app_name: 'publica.la Reader',
        },

        // Test mode configuration
        testMode: {
            anonymousSession: false,
            enforceConcurrencyLimit: false,
            debugEnforceConcurrencyLimit: false,
            featureFlags: 'StandAloneAllFeatures',
        },

        // EPUB configuration (only when fileType is 'epub')
        epub: epubConfig
            ? {
                  filePath: epubConfig.path,
                  name: epubConfig.name,
                  description: epubConfig.description,
                  cover: epubConfig.cover,
              }
            : null,
    };
}

/**
 * Apply test data to the store
 * Helper function to commit all test data to appropriate store locations.
 * @param {object} [devConfig] Resolved dev query-config (see devConfig.js). When
 *   provided, overrides the static testMode flags so the DEV menu / URL params
 *   can simulate host-only modes (preview, anonymous, concurrency, embedded).
 */
export function applyTestDataToStore(store, testData, devConfig = null) {
    // Set issue data
    store.commit('setFakeIssue', { issue: testData.issue });

    // Set publication data
    store.commit('title', { title: testData.publication.title });
    store.commit('filesUrls', { files_urls: testData.publication.files_urls });
    store.commit('filesInfo', { files_info: testData.publication.files_info });
    store.commit('tableOfContents', { table_of_contents: testData.publication.table_of_contents });
    if (testData.publication.cover) {
        store.commit('publicationCover', { cover: testData.publication.cover });
    }

    // Set audiobook data
    store.commit('loadingAudiobookPlaylist', { playlist: testData.audiobook.playlist });

    // Set highlights (commented out in original code)
    // store.commit('setHighlights', { highlights: testData.highlights });

    // Set promote app data
    store.commit('setPromoteAppData', testData.promoteApp);

    // Set test mode configurations. Dev query-config (when present) overrides
    // the static defaults so host-only modes can be simulated standalone.
    const anonymous = devConfig ? devConfig.anonymous : testData.testMode.anonymousSession;
    const enforceConcurrency = devConfig ? devConfig.concurrency : testData.testMode.enforceConcurrencyLimit;
    const debugConcurrency = devConfig ? devConfig.concurrencyDebug : testData.testMode.debugEnforceConcurrencyLimit;

    store.commit('anonymousSession', { anonymous_session: anonymous });
    store.commit('setEnforceConcurrencyLimit', enforceConcurrency);
    store.commit('setDebugEnforceConcurrencyLimit', debugConcurrency);
    store.commit(testData.testMode.featureFlags);

    if (devConfig) {
        // Simulate host-driven modes that normally arrive via Delfino initData.
        store.commit('previewMode', { previewMode: devConfig.preview });
        store.commit('customPreview', { customPreview: devConfig.customPreview });
        store.commit('embeddedMode', { embeddedMode: devConfig.embedded });
        store.commit('setAddOnsEditing', { enabled: devConfig.addOnsEditing });
        store.commit('setAddOnBatchSupported', { supported: true });
        store.commit('setInitialPanel', devConfig.addOnsPanel ? 'addOns' : null);

        // Tenant flag has no host in dev; simulate it from the knob. The knob
        // gates the note editor's rich input alone: the add-on editor is rich
        // regardless, so its embed hosts are seeded either way.
        store.commit('setRichTextEditorEnabled', { rich_text_editor_enabled: devConfig.richText });
        store.commit('setRichTextEmbedHosts', { embed_hosts: RICH_TEXT_EMBED_HOSTS });
    }

    // After the modes, as the host's content structure follows its initData:
    // setIssueArticles reads the preview flag.
    store.commit('setIssueArticles', { articles: testData.articles });

    // Set EPUB configuration if provided
    if (testData.epub) {
        store.commit('setEpubFilePath', { filePath: testData.epub.filePath });

        if (devConfig) {
            // EPUB layout has two sources of truth: the active scroll/spread
            // settings (store getters) and userSettings.epub.layout, which
            // EpubViewer reads directly at mount. Set both.
            store.commit('setDevEpubLayout', devConfig.epubLayout);
        }
    }
}

export default getTestData;
